import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';
import { toast } from 'react-hot-toast';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface IndexStatus {
  success: boolean;
  is_indexed: boolean;
  last_indexed_at: string | null;
  index_id: string | null;
  total_conversations: number;
  message: string;
}

interface ChatHistoryResponse {
  success: boolean;
  chat_history: {
    current_page: number;
    data: Array<{
      id: number;
      user_message: string;
      ai_response: string;
      created_at: string;
    }>;
    first_page_url: string;
    last_page: number;
    last_page_url: string;
    next_page_url: string | null;
    prev_page_url: string | null;
    total: number;
    per_page: number;
  };
}

interface ChatSession {
  id: number;
  session_id: string;
  title: string;
  created_at: string;
  updated_at: string;
  preview?: string;
  message_count: number;
}

interface ChatState {
  messages: ChatMessage[];
  chatHistory: ChatHistoryResponse['chat_history'] | null;
  currentPage: number;
  isLoading: boolean;
  isIndexing: boolean;
  isNavigating: boolean;
  indexStatus: IndexStatus | null;
  error: string | null;
  chatSessions: ChatSession[];
  currentSessionId: string | null;
  isLoadingSessions: boolean;
  sessionError: string | null;
  isExporting: boolean;
  exportError: string | null;
}

const initialState: ChatState = {
  messages: [],
  chatHistory: null,
  currentPage: 1,
  isLoading: false,
  isIndexing: false,
  isNavigating: false,
  indexStatus: null,
  error: null,
  chatSessions: [],
  currentSessionId: null,
  isLoadingSessions: false,
  sessionError: null,
  isExporting: false,
  exportError: null,
};

// Async thunks
export const indexReplies = createAsyncThunk(
  'chat/indexReplies',
  async (projectIdOrSlug: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/projects/${projectIdOrSlug}/index-replies`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to index replies');
    }
  }
);

export const checkIndexStatus = createAsyncThunk(
  'chat/checkIndexStatus',
  async (projectIdOrSlug: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/projects/${projectIdOrSlug}/index-status`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get index status');
    }
  }
);

export const startNewChatSession = createAsyncThunk(
  'chat/startNewChatSession',
  async ({ projectIdOrSlug, title }: { projectIdOrSlug: string; title?: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/projects/${projectIdOrSlug}/chat-sessions/start`, {
        title
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to start new chat session');
    }
  }
);

export const getChatSessions = createAsyncThunk(
  'chat/getChatSessions',
  async (projectIdOrSlug: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/projects/${projectIdOrSlug}/chat-sessions`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get chat sessions');
    }
  }
);

export const getChatSessionMessages = createAsyncThunk(
  'chat/getChatSessionMessages',
  async ({ projectIdOrSlug, sessionId }: { projectIdOrSlug: string; sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/projects/${projectIdOrSlug}/chat-sessions/${sessionId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get chat session messages');
    }
  }
);

export const updateChatSessionTitle = createAsyncThunk(
  'chat/updateChatSessionTitle',
  async ({ projectIdOrSlug, sessionId, title }: { projectIdOrSlug: string; sessionId: string; title: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/projects/${projectIdOrSlug}/chat-sessions/${sessionId}/title`, {
        title
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update chat session title');
    }
  }
);

export const deleteChatSession = createAsyncThunk(
  'chat/deleteChatSession',
  async ({ projectIdOrSlug, sessionId }: { projectIdOrSlug: string; sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/projects/${projectIdOrSlug}/chat-sessions/${sessionId}`);
      return { ...response.data, sessionId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete chat session');
    }
  }
);

export const chatWithReplies = createAsyncThunk(
  'chat/chatWithReplies',
  async ({ 
    projectIdOrSlug, 
    message, 
    sessionId 
  }: { 
    projectIdOrSlug: string; 
    message: string; 
    sessionId?: string 
  }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/projects/${projectIdOrSlug}/chat-with-replies`, {
        message,
        session_id: sessionId
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get chat response');
    }
  }
);

export const exportChatHistory = createAsyncThunk(
  'chat/exportChatHistory',
  async ({ 
    projectIdOrSlug, 
    sessionId 
  }: { 
    projectIdOrSlug: string; 
    sessionId: string 
  }, { rejectWithValue }) => {
    try {
      // Use responseType: 'blob' to handle binary PDF data
      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/chat-sessions/${sessionId}/export`, 
        { responseType: 'blob' }
      );
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create file name for download
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'chat-history.pdf';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch.length === 2) filename = filenameMatch[1];
      }
      
      // Create temporary anchor to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export chat history');
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.messages = [];
    },
    addMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    setCurrentSessionId: (state, action) => {
      state.currentSessionId = action.payload;
    },
    clearChatState: (state) => {
      state.messages = [];
      state.chatHistory = null;
      state.currentPage = 1;
      state.isLoading = false;
      state.isIndexing = false;
      state.isNavigating = false;
      state.indexStatus = null;
      state.error = null;
      state.chatSessions = [];
      state.currentSessionId = null;
      state.isLoadingSessions = false;
      state.sessionError = null;
      state.isExporting = false;
      state.exportError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Index Replies
      .addCase(indexReplies.pending, (state) => {
        state.isIndexing = true;
        state.error = null;
      })
      .addCase(indexReplies.fulfilled, (state) => {
        state.isIndexing = false;
        toast.success('Replies indexed successfully');
      })
      .addCase(indexReplies.rejected, (state, action) => {
        state.isIndexing = false;
        state.error = action.payload as string;
        toast.error(state.error || 'Failed to index replies');
      })
      // Check Index Status
      .addCase(checkIndexStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkIndexStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success) {
          state.indexStatus = action.payload;
        } else {
          state.error = action.payload.message;
          toast.error(state.error || 'Failed to check index status');
        }
      })
      .addCase(checkIndexStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        toast.error(state.error || 'Failed to check index status');
      })
      // Start New Chat Session
      .addCase(startNewChatSession.pending, (state) => {
        state.isLoadingSessions = true;
        state.sessionError = null;
      })
      .addCase(startNewChatSession.fulfilled, (state, action) => {
        state.isLoadingSessions = false;
        if (action.payload.success) {
          state.currentSessionId = action.payload.session_id;
          state.messages = []; // Clear messages for new session
          // Add to sessions list if not already there
          const sessionExists = state.chatSessions.some(s => s.session_id === action.payload.session_id);
          if (!sessionExists) {
            state.chatSessions.unshift({
              id: action.payload.chat_id,
              session_id: action.payload.session_id,
              title: action.payload.title,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              message_count: 0
            });
          }
        }
      })
      .addCase(startNewChatSession.rejected, (state, action) => {
        state.isLoadingSessions = false;
        state.sessionError = action.payload as string;
        toast.error(state.sessionError || 'Failed to start new chat session');
      })
      
      // Get Chat Sessions
      .addCase(getChatSessions.pending, (state) => {
        state.isLoadingSessions = true;
        state.sessionError = null;
      })
      .addCase(getChatSessions.fulfilled, (state, action) => {
        state.isLoadingSessions = false;
        if (action.payload.success) {
          state.chatSessions = action.payload.sessions;
          // If there are sessions and no current session is selected, select the first one
          if (action.payload.sessions.length > 0 && !state.currentSessionId) {
            state.currentSessionId = action.payload.sessions[0].session_id;
          }
        }
      })
      .addCase(getChatSessions.rejected, (state, action) => {
        state.isLoadingSessions = false;
        state.sessionError = action.payload as string;
        toast.error(state.sessionError || 'Failed to get chat sessions');
      })
      
      // Get Chat Session Messages
      .addCase(getChatSessionMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getChatSessionMessages.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success) {
          // Convert message format to match our app's format
          const messages = action.payload.messages.flatMap((msg: any) => [
            { role: 'user', content: msg.user_message },
            { role: 'assistant', content: msg.ai_response }
          ]);
          state.messages = messages;
          state.currentSessionId = action.payload.session.session_id;
        }
      })
      .addCase(getChatSessionMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        toast.error(state.error || 'Failed to get chat session messages');
      })
      
      // Update Chat Session Title
      .addCase(updateChatSessionTitle.fulfilled, (state, action) => {
        if (action.payload.success) {
          // Update the title in the sessions list
          const sessionIndex = state.chatSessions.findIndex(
            s => s.session_id === action.payload.session.session_id
          );
          if (sessionIndex !== -1) {
            state.chatSessions[sessionIndex].title = action.payload.session.title;
          }
          toast.success('Chat title updated');
        }
      })
      .addCase(updateChatSessionTitle.rejected, (state, action) => {
        state.sessionError = action.payload as string;
        toast.error(state.sessionError || 'Failed to update chat title');
      })
      
      // Delete Chat Session
      .addCase(deleteChatSession.fulfilled, (state, action) => {
        if (action.payload.success) {
          // Remove the session from the list
          state.chatSessions = state.chatSessions.filter(
            s => s.session_id !== action.payload.sessionId
          );
          
          // If the current session was deleted, select another one if available
          if (state.currentSessionId === action.payload.sessionId) {
            state.currentSessionId = state.chatSessions.length > 0 ? 
              state.chatSessions[0].session_id : null;
            state.messages = [];
          }
          
          toast.success('Chat session deleted');
        }
      })
      .addCase(deleteChatSession.rejected, (state, action) => {
        state.sessionError = action.payload as string;
        toast.error(state.sessionError || 'Failed to delete chat session');
      })
      .addCase(chatWithReplies.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(chatWithReplies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        toast.error(state.error || 'Failed to get chat response');
      })
      // Add session_id to response in chatWithReplies.fulfilled
      .addCase(chatWithReplies.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success) {
          // Add both user message and AI response to messages
          state.messages.push(
            {
              role: 'user',
              content: action.payload.user_message
            },
            {
              role: 'assistant',
              content: action.payload.ai_response
            }
          );
          
          // Set current session ID if returned from backend
          if (action.payload.session_id && !state.currentSessionId) {
            state.currentSessionId = action.payload.session_id;
            
            // Add to sessions list if not already there
            const sessionExists = state.chatSessions.some(s => s.session_id === action.payload.session_id);
            if (!sessionExists) {
              state.chatSessions.unshift({
                id: action.payload.chat_id,
                session_id: action.payload.session_id,
                title: `New Chat ${new Date().toLocaleString()}`,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                preview: action.payload.user_message.substring(0, 50),
                message_count: 1
              });
            } else {
              // Update the existing session
              const sessionIndex = state.chatSessions.findIndex(s => s.session_id === action.payload.session_id);
              if (sessionIndex !== -1) {
                state.chatSessions[sessionIndex].preview = action.payload.user_message.substring(0, 50);
                state.chatSessions[sessionIndex].updated_at = new Date().toISOString();
                state.chatSessions[sessionIndex].message_count += 1;
                
                // Move this session to the top of the list
                const updatedSession = state.chatSessions[sessionIndex];
                state.chatSessions.splice(sessionIndex, 1);
                state.chatSessions.unshift(updatedSession);
              }
            }
          }
        }
      })
      // Export Chat History
      .addCase(exportChatHistory.pending, (state) => {
        state.isExporting = true;
        state.exportError = null;
      })
      .addCase(exportChatHistory.fulfilled, (state) => {
        state.isExporting = false;
        toast.success('Chat history exported successfully');
      })
      .addCase(exportChatHistory.rejected, (state, action) => {
        state.isExporting = false;
        state.exportError = action.payload as string;
        toast.error(state.exportError || 'Failed to export chat history');
      });
  },
});

export const { clearMessages, addMessage, setCurrentSessionId } = chatSlice.actions;

// Add new action to clear all chat state
export const clearChatState = chatSlice.actions.clearChatState;

export default chatSlice.reducer; 